import { useEffect, useState } from "react";

import { Column, Row, TopbarContainer, TopbarTitle } from "@/components";
import { DashboardUserSelector } from "@/components/dashboard/DashboardUserSelector";
import { EarningsChartSection } from "@/components/earnings/EarningsChartSection";
import { EarningsHighestValueReqsSection } from "@/components/earnings/EarningsHighestValueReqsSection";
import { EarningsInterviewsSection } from "@/components/earnings/EarningsInterviewsSection";
import { EarningsOutstandingOffersSection } from "@/components/earnings/EarningsOutstandingOffersSection";
import { EarningsTrackerSection } from "@/components/earnings/EarningsTrackerSection";

import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useSetTitle } from "@/hooks/useSetTitle";
import { EARNINGS_ADMIN_VIEWER_USER_IDS } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { DashboardSelectedUser } from "@/types";

export const EarningsPage = () => {
  /* Database CRUD Operations */
  // Read
  const currentUser = useGetCurrentUser();

  /* React Hooks */
  const [selectedUserId, setSelectedUserId] = useState<number | null>(
    currentUser?.id ?? null,
  );

  useSetTitle({ title: "Earnings" });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.EARNINGS);
  }, []);

  /* Helpers */
  const isAdminViewer =
    !!currentUser && EARNINGS_ADMIN_VIEWER_USER_IDS.includes(currentUser.id);

  /* Handlers */
  // Selector hides the "All staffers" option, so only numeric ids arrive
  const handleSelectedUserChange = (next: DashboardSelectedUser) => {
    if (typeof next === "number") {
      setSelectedUserId(next);
    }
  };

  /* Render */
  const render = () => (
    <Column className="h-svh min-w-0 flex-1 overflow-y-auto bg-slate-50">
      <TopbarContainer className="!h-auto border-b-0 !px-10 !pb-3 !pt-9">
        <Row className="w-full items-center justify-between gap-4">
          <TopbarTitle className="text-xl font-semibold text-slate-900">
            Earnings
          </TopbarTitle>
          {currentUser && isAdminViewer ? (
            <DashboardUserSelector
              includeAllUsers={false}
              onValueChange={handleSelectedUserChange}
              screen={MIXPANEL_SCREENS.EARNINGS}
              value={selectedUserId ?? currentUser.id}
            />
          ) : null}
        </Row>
      </TopbarContainer>
      <Column className="gap-8 px-10 pb-[240px] pt-4">
        <EarningsChartSection selectedUserId={selectedUserId ?? undefined} />
        <EarningsOutstandingOffersSection
          selectedUserId={selectedUserId ?? undefined}
        />
        <EarningsInterviewsSection
          selectedUserId={selectedUserId ?? undefined}
        />
        <EarningsHighestValueReqsSection
          selectedUserId={selectedUserId ?? undefined}
        />
        <EarningsTrackerSection selectedUserId={selectedUserId ?? undefined} />
      </Column>
    </Column>
  );

  return render();
};
